// src/scenes/EnterprisePhilosophyFlowScene.js
// Enterprise-grade Philosophy Flow scene with proper mobile architecture
// Fortune 500 quality with comprehensive error handling and clean architecture

import Phaser from 'phaser';
import BaseMenuScene from "./BaseMenuScene.js";
import { auth, db } from "../firebaseConfig.js";
import claudeAPIService from "../services/claudeAPIService.js";
import { hasSpecialAccess } from '../services/specialAccessService.js';

// REMOVED: EnterpriseMobileOverlay - now using UnifiedMobileSystem

// Enterprise-grade constants
const PHILOSOPHY_FLOW_STEPS = {
    CATEGORY_SELECTION: 1,
    EMOTION_SELECTION: 2,
    COLOR_SELECTION: 3,
    SLEEP_SELECTION: 4,
    GENERATING: 5,
    DISPLAY_QUOTE: 6,
    RATE_LIMITED: 7
};

const EMOTION_CATEGORIES = {
    'positive-high': [
        { emoji: '🥳', label: 'Excited', description: 'Feeling energized and enthusiastic' },
        { emoji: '🤩', label: 'Inspired', description: 'Feeling motivated and creative' },
        { emoji: '😎', label: 'Confident', description: 'Feeling assured and capable' },
        { emoji: '🔥', label: 'Energized', description: 'Feeling powerful and driven' }
    ],
    'positive-calm': [
        { emoji: '😌', label: 'Peaceful', description: 'Feeling calm and centered' },
        { emoji: '😊', label: 'Content', description: 'Feeling satisfied and happy' },
        { emoji: '🤔', label: 'Thoughtful', description: 'Feeling reflective and contemplative' },
        { emoji: '😇', label: 'Grateful', description: 'Feeling appreciative and blessed' }
    ],
    'challenging': [
        { emoji: '😟', label: 'Worried', description: 'Feeling concerned about outcomes' },
        { emoji: '😢', label: 'Sad', description: 'Feeling down or melancholic' },
        { emoji: '😨', label: 'Anxious', description: 'Feeling nervous or uncertain' },
        { emoji: '😤', label: 'Frustrated', description: 'Feeling blocked or hindered' }
    ],
    'intense': [
        { emoji: '😠', label: 'Angry', description: 'Feeling upset or irritated' },
        { emoji: '😡', label: 'Furious', description: 'Feeling intense anger' },
        { emoji: '🤯', label: 'Overwhelmed', description: 'Feeling too much at once' },
        { emoji: '😱', label: 'Shocked', description: 'Feeling surprised or stunned' }
    ]
};

const EXECUTIVE_COLOR_PALETTE = [
    { hex: '#FF6B6B', name: 'Executive Red', mood: 'Power' },
    { hex: '#FFA500', name: 'Strategic Orange', mood: 'Innovation' },
    { hex: '#FFD700', name: 'Leadership Gold', mood: 'Excellence' },
    { hex: '#98D8C8', name: 'Clarity Mint', mood: 'Focus' },
    { hex: '#00D4FF', name: 'DeckOps Blue', mood: 'Intelligence' },
    { hex: '#8B5CF6', name: 'Visionary Purple', mood: 'Insight' },
    { hex: '#10B981', name: 'Growth Green', mood: 'Progress' },
    { hex: '#EC4899', name: 'Creative Pink', mood: 'Innovation' },
    { hex: '#F59E0B', name: 'Ambition Amber', mood: 'Drive' },
    { hex: '#6366F1', name: 'Trust Indigo', mood: 'Stability' },
    { hex: '#14B8A6', name: 'Balance Teal', mood: 'Harmony' },
    { hex: '#F43F5E', name: 'Passion Rose', mood: 'Energy' }
];

export default class EnterprisePhilosophyFlowScene extends BaseMenuScene {
    constructor() {
        super({ key: "EnterprisePhilosophyFlowScene" });
        
        // UNIFIED: Use centralized mobile detection
        import('../services/MobileDetectionService.js').then(({ MobileDetectionService }) => {
            this.isMobile = MobileDetectionService.isMobile();
        });
        
        // Scene state
        this.isInitialized = false;
        this.mobileStrategy = null;
        this.dependencies = new Map();
        
        // Philosophy Flow state
        this.currentStep = PHILOSOPHY_FLOW_STEPS.CATEGORY_SELECTION;
        this.sessionData = {
            selectedCategory: null,
            selectedEmotion: null,
            selectedColor: null,
            selectedSleep: null,
            quoteData: null,
            sessionId: this._generateSessionId()
        };
        
        // User state
        this.isAdmin = false;
        this.isRateLimited = false;
        this.lastQuoteTimestamp = null;
        
        // Services
        this.claudeService = new claudeAPIService();
        
        // Event subscriptions for cleanup
        this.eventSubscriptions = [];
    }
    
    /**
     * Initialize scene with enterprise architecture
     */
    async init(data) {
        try {
                super.init(data);
                
                console.log(`🎭 EnterprisePhilosophyFlowScene: Initializing session ${this.sessionData.sessionId}`);
                
                // Setup dependencies
                await this._setupDependencies();
                
                // Load user state
                await this._loadUserState();
                
                // Initialize mobile strategy if needed
                if (this.isMobile) {
                // this.mobileStrategy = MobileStrategyFactory.createStrategy(this);
                // await this.mobileStrategy.initialize();
                }
                
                // Setup event subscriptions
                this._setupEventSubscriptions();
                
                this.isInitialized = true;
                console.log(`✅ EnterprisePhilosophyFlowScene: Initialized successfully`);
        } catch (error) {
            console.error('Failed to initialize EnterprisePhilosophyFlowScene:', error);
                this._handleInitializationFailure();
        }
    }
    
    /**
     * Create scene with enterprise architecture
     */
    create() {
        try {
            console.log('🎭 EnterprisePhilosophyFlowScene: Creating with enterprise architecture');
            super.create();
            
            // Create interface based on device type
            if (this.isMobile) {
                this._createMobileInterface();
            } else {
                this._createDesktopInterface();
            }
            
            console.log('🎭 EnterprisePhilosophyFlowScene: Interface created successfully');
        } catch (error) {
            console.error('Failed to create EnterprisePhilosophyFlowScene:', error);
            this._createFallbackInterface();
        }
    }
    
    /**
     * Setup dependencies
     * @private
     */
    async _setupDependencies() {
        // Analytics dependency
        if (window.mobileAnalytics) {
            this.dependencies.set('analytics', window.mobileAnalytics);
        }
        
        // Claude API service
        this.dependencies.set('claudeService', this.claudeService);
        
        console.log(`✅ Philosophy Flow dependencies setup: ${this.dependencies.size} services`);
    }
    
    /**
     * Load user state and rate limiting
     * @private
     */
    async _loadUserState() {
        const user = auth.currentUser;
        const specialAccess = hasSpecialAccess();
        
        if (user) {
            const profileDoc = await db.collection("userProfiles").doc(user.uid).get();
            if (profileDoc.exists) {
                const profileData = profileDoc.data();
                this.lastQuoteTimestamp = profileData.lastPhilosophyQuoteTimestamp || null;
                this.isAdmin = profileData.role === 'admin';
                
                // Check rate limiting for non-admin users
                if (!this.isAdmin && this.lastQuoteTimestamp) {
                    this._checkRateLimit();
                }
            }
        } else if (specialAccess) {
            this.isAdmin = true; // Special access gets admin privileges
        }
        
        console.log(`✅ User state loaded - Admin: ${this.isAdmin}, Rate Limited: ${this.isRateLimited}`);
    }
    
    /**
     * Check rate limiting
     * @private
     */
    _checkRateLimit() {
        if (!this.lastQuoteTimestamp) return;
        
        const now = new Date();
        const lastQuote = this.lastQuoteTimestamp.toDate();
        const timeDiff = now - lastQuote;
        const hoursSinceLastQuote = timeDiff / (1000 * 60 * 60);
        
        // 24-hour rate limit for non-admin users
        this.isRateLimited = hoursSinceLastQuote < 24;
        
        if (this.isRateLimited) {
            const hoursRemaining = Math.ceil(24 - hoursSinceLastQuote);
            console.log(`⏰ Rate limited - ${hoursRemaining} hours remaining`);
        }
    }
    
    /**
     * Setup event subscriptions for mobile UI interactions
     * @private
     */
    _setupEventSubscriptions() {
        // Category selection
        // Use Phaser's built-in event system
        const unsubscribeCategory = this.events.on('mobile.category.select',
            this._handleCategorySelection.bind(this)
        );
        this.eventSubscriptions.push(unsubscribeCategory);
        
        // Emotion selection
        const unsubscribeEmotion = this.eventBus.on('mobile.emotion.select',
            this._handleEmotionSelection.bind(this)
        );
        this.eventSubscriptions.push(unsubscribeEmotion);
        
        // Color selection
        const unsubscribeColor = this.eventBus.on('mobile.color.select',
            this._handleColorSelection.bind(this)
        );
        this.eventSubscriptions.push(unsubscribeColor);
        
        // Back navigation
        const unsubscribeBack = this.eventBus.on('mobile.navigation.back',
            this._handleBackNavigation.bind(this)
        );
        this.eventSubscriptions.push(unsubscribeBack);
        
        // Quote generation (connect to existing Claude API logic)
        const unsubscribeQuote = this.eventBus.on('mobileGenerateQuote',
            this._handleQuoteGeneration.bind(this)
        );
        this.eventSubscriptions.push(unsubscribeQuote);
        
        // Start Philosophy Flow from dashboard
        const unsubscribeStart = this.eventBus.on('mobileStartPhilosophyFlow',
            this._handlePhilosophyFlowStart.bind(this)
        );
        this.eventSubscriptions.push(unsubscribeStart);
    }
    
    /**
     * Handle Philosophy Flow start from dashboard
     * @param {Object} data - Event data
     * @private
     */
    _handlePhilosophyFlowStart(data) {
        console.log('🎭 Starting Philosophy Flow from mobile dashboard');
        // Philosophy Flow scene is already active, just ensure mobile interface is ready
        this._showCategoryScreen();
    }
    
    /**
     * Handle quote generation with existing Claude API
     * @param {Object} data - Event data containing selections
     * @private
     */
    _handleQuoteGeneration(data) {
        try {
                console.log('🎭 Generating quote with selections:', data);
                
                // Store selections
                this.sessionData.selectedCategory = data.category;
                this.sessionData.selectedEmotion = data.emotion;
                this.sessionData.selectedColor = data.color;
                
                // Generate quote using existing logic
                this._generatePhilosophicalInsight();
        } catch (error) {
            console.warn('Failed to handle quote generation:', error);
        }
    }
    
    /**
     * Create mobile interface
     * @private
     */
    _createMobileInterface() {
        // REMOVED: EnterpriseMobileOverlay usage
        // Mobile functionality now handled by UnifiedMobileSystem
        console.log('📱 Philosophy Flow mobile interface handled by UnifiedMobileSystem');
        
        // Create simple mobile interface for philosophy flow
        this._createSimpleMobilePhilosophyInterface();
    }
    
    /**
     * Create simple mobile interface for philosophy flow
     * @private
     */
    _createSimpleMobilePhilosophyInterface() {
        console.log('🎭 Creating simple mobile philosophy flow interface...');
        
        // Create simple overlay for philosophy flow
        const overlay = document.createElement('div');
        overlay.id = 'philosophy-mobile-overlay';
        overlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            z-index: 10000;
            background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
            color: white;
            font-family: 'Segoe UI', sans-serif;
            padding: 20px;
            overflow-y: auto;
            -webkit-overflow-scrolling: touch;
        `;
        
        overlay.innerHTML = `
            <div style="text-align: center; padding: 40px;">
                <h1 style="color: #00d4ff; margin-bottom: 20px;">🎭 Philosophy Flow</h1>
                <p style="margin-bottom: 20px;">Enterprise Philosophy Training</p>
                <button onclick="history.back()" style="
                    background: #00d4ff;
                    color: white;
                    border: none;
                    padding: 12px 24px;
                    border-radius: 8px;
                    font-size: 16px;
                ">← Back to Dashboard</button>
            </div>
        `;
        
        document.body.appendChild(overlay);
        this.mobileOverlay = overlay;
        
        console.log('✅ Simple philosophy flow mobile interface created');
    }
    
    /**
     * Create desktop interface (legacy fallback)
     * @private
     */
    _createDesktopInterface() {
        // Use existing Philosophy Flow DOM creation for desktop
        console.log('🖥️ Creating desktop Philosophy Flow interface');
        // Legacy implementation would go here
    }
    
    /**
     * Handle category selection
     * @param {Object} data - Event data
     * @private
     */
    _handleCategorySelection(data) {
        try {
                console.log(`🎭 Category selected: ${data.category}`);
                
                this.sessionData.selectedCategory = data.category;
                this.currentStep = PHILOSOPHY_FLOW_STEPS.EMOTION_SELECTION;
                
                // Show specific emotions for category
                this._showSpecificEmotions(data.category);
                
                // Track analytics
                const analytics = this.dependencies.get('analytics');
                if (analytics) {
                    analytics.trackEvent('philosophy_flow_category_selected', {
                        category: data.category,
                        sessionId: this.sessionData.sessionId
                    });
                }
        } catch (error) {
            console.warn('Failed to handle category selection:', error);
        }
    }
    
    /**
     * Handle emotion selection
     * @param {Object} data - Event data
     * @private
     */
    _handleEmotionSelection(data) {
        try {
                console.log(`🎭 Emotion selected: ${data.emotion}`);
                
                this.sessionData.selectedEmotion = data.emotion;
                this.currentStep = PHILOSOPHY_FLOW_STEPS.COLOR_SELECTION;
                
                // Show color palette
                this._showColorPalette();
                
                // Track analytics
                const analytics = this.dependencies.get('analytics');
                if (analytics) {
                    analytics.trackEvent('philosophy_flow_emotion_selected', {
                        emotion: data.emotion,
                        sessionId: this.sessionData.sessionId
                    });
                }
        } catch (error) {
            console.warn('Failed to handle emotion selection:', error);
        }
    }
    
    /**
     * Handle color selection
     * @param {Object} data - Event data
     * @private
     */
    _handleColorSelection(data) {
        try {
                console.log(`🎭 Color selected: ${data.name} (${data.hex})`);
                
                this.sessionData.selectedColor = data;
                this.currentStep = PHILOSOPHY_FLOW_STEPS.GENERATING;
                
                // Generate philosophical insight
                this._generatePhilosophicalInsight();
                
                // Track analytics
                const analytics = this.dependencies.get('analytics');
                if (analytics) {
                    analytics.trackEvent('philosophy_flow_color_selected', {
                        color: data.name,
                        mood: data.mood,
                        sessionId: this.sessionData.sessionId
                    });
                }
        } catch (error) {
            console.warn('Failed to handle color selection:', error);
        }
    }
    
    /**
     * Handle back navigation
     * @private
     */
    _handleBackNavigation() {
        try {
                console.log('🎭 Back navigation requested');
                
                // Return to authenticated menu
                this._returnToAuthenticatedMenu();
        } catch (error) {
            console.warn('Failed to handle back navigation:', error);
        }
    }
    
    /**
     * Show specific emotions for selected category
     * @param {string} categoryId - Category identifier
     * @private
     */
    _showSpecificEmotions(categoryId) {
        const overlay = this.componentManager.get(`${this.scene.key}_mobileOverlay`);
        if (!overlay) return;
        
        const emotions = EMOTION_CATEGORIES[categoryId] || [];
        
        const emotionHTML = `
            <div class="game-container">
                <!-- Header -->
                <header class="game-header">
                    <div class="header-content">
                        <button class="back-button" aria-label="Back to Categories">←</button>
                        <div class="game-title">Philosophy Flow</div>
                        <div class="eq-score">EQ: 742</div>
                    </div>
                </header>

                <!-- Progress -->
                <div class="progress-section" style="background: rgba(0, 0, 0, 0.3); padding: var(--space-lg);">
                    <div class="step-indicators" style="display: flex; justify-content: center; gap: var(--space-md);">
                        <div class="step-dot completed" style="width: 8px; height: 8px; border-radius: 50%; background: var(--success);"></div>
                        <div class="step-dot active" style="width: 24px; height: 8px; border-radius: 4px; background: var(--primary-solid);"></div>
                        <div class="step-dot" style="width: 8px; height: 8px; border-radius: 50%; background: rgba(255, 255, 255, 0.2);"></div>
                        <div class="step-dot" style="width: 8px; height: 8px; border-radius: 50%; background: rgba(255, 255, 255, 0.2);"></div>
                    </div>
                    <div class="step-label" style="text-align: center; font-size: 12px; color: var(--text-muted); text-transform: uppercase;">Step 2: Specific Emotion</div>
                </div>

                <!-- Emotion Grid -->
                <div class="game-content" style="padding: var(--space-2xl); padding-top: 0;">
                    <h2 class="screen-title" style="font-size: 22px; font-weight: 700; color: var(--text-primary); margin-bottom: var(--space-sm);">
                        Now, be more specific
                    </h2>
                    <p class="screen-subtitle" style="font-size: 14px; color: var(--text-muted); margin-bottom: var(--space-2xl);">
                        Select the emotion that most accurately represents your feeling
                    </p>
                    
                    <div class="emotions-grid" style="display: grid; grid-template-columns: repeat(2, 1fr); gap: var(--space-lg);">
                        ${emotions.map(emotion => `
                            <button class="emotion-button" data-emotion="${emotion.label}" style="
                                background: var(--card-primary);
                                backdrop-filter: blur(12px);
                                border: 2px solid rgba(255, 255, 255, 0.1);
                                border-radius: var(--radius-md);
                                padding: var(--space-xl);
                                min-height: 90px;
                                display: flex;
                                flex-direction: column;
                                align-items: center;
                                justify-content: center;
                                cursor: pointer;
                                color: var(--text-primary);
                            ">
                                <span class="emotion-emoji" style="font-size: 36px; margin-bottom: var(--space-sm);">${emotion.emoji}</span>
                                <span class="emotion-label" style="font-size: 14px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px;">${emotion.label}</span>
                            </button>
                        `).join('')}
                    </div>
                </div>
            </div>
        `;
        
        overlay.component.setContent(emotionHTML);
    }
    
    /**
     * Show color palette
     * @private
     */
    _showColorPalette() {
        const overlay = this.componentManager.get(`${this.scene.key}_mobileOverlay`);
        if (!overlay) return;
        
        const colorHTML = `
            <div class="game-container">
                <!-- Header -->
                <header class="game-header">
                    <div class="header-content">
                        <button class="back-button" aria-label="Back to Emotions">←</button>
                        <div class="game-title">Philosophy Flow</div>
                        <div class="eq-score">EQ: 742</div>
                    </div>
                </header>

                <!-- Progress -->
                <div class="progress-section" style="background: rgba(0, 0, 0, 0.3); padding: var(--space-lg);">
                    <div class="step-indicators" style="display: flex; justify-content: center; gap: var(--space-md);">
                        <div class="step-dot completed" style="width: 8px; height: 8px; border-radius: 50%; background: var(--success);"></div>
                        <div class="step-dot completed" style="width: 8px; height: 8px; border-radius: 50%; background: var(--success);"></div>
                        <div class="step-dot active" style="width: 24px; height: 8px; border-radius: 4px; background: var(--primary-solid);"></div>
                        <div class="step-dot" style="width: 8px; height: 8px; border-radius: 50%; background: rgba(255, 255, 255, 0.2);"></div>
                    </div>
                    <div class="step-label" style="text-align: center; font-size: 12px; color: var(--text-muted); text-transform: uppercase;">Step 3: Energy Color</div>
                </div>

                <!-- Color Palette -->
                <div class="game-content" style="padding: var(--space-2xl); padding-top: 0;">
                    <h2 class="screen-title" style="font-size: 22px; font-weight: 700; color: var(--text-primary); margin-bottom: var(--space-sm);">
                        Choose Your Energy Color
                    </h2>
                    <p class="screen-subtitle" style="font-size: 14px; color: var(--text-muted); margin-bottom: var(--space-2xl);">
                        Select a color that resonates with your emotional state
                    </p>
                    
                    <div class="color-palette" style="display: grid; grid-template-columns: repeat(4, 1fr); gap: var(--space-md);">
                        ${EXECUTIVE_COLOR_PALETTE.map(color => `
                            <div class="color-swatch" 
                                 data-color="${color.hex}"
                                 data-name="${color.name}" 
                                 data-mood="${color.mood}"
                                 style="
                                     aspect-ratio: 1;
                                     background: ${color.hex};
                                     border-radius: var(--radius-md);
                                     border: 3px solid transparent;
                                     cursor: pointer;
                                     transition: var(--transition-normal);
                                     box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
                                 ">
                            </div>
                        `).join('')}
                    </div>

                    <div class="color-preview" style="
                        background: var(--card-primary);
                        backdrop-filter: blur(12px);
                        border: 1px solid rgba(255, 255, 255, 0.1);
                        border-radius: var(--radius-lg);
                        padding: var(--space-xl);
                        display: flex;
                        align-items: center;
                        gap: var(--space-xl);
                        margin-top: var(--space-2xl);
                    ">
                        <div class="color-preview-swatch" id="selectedColorPreview" style="
                            width: 64px;
                            height: 64px;
                            border-radius: var(--radius-md);
                            background: var(--primary-solid);
                            box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
                        "></div>
                        <div class="color-info">
                            <h3 id="colorName" style="font-size: 16px; font-weight: 600; margin-bottom: var(--space-xs); color: var(--text-primary);">
                                Select a color
                            </h3>
                            <p id="colorMood" style="font-size: 14px; color: var(--text-muted);">
                                Choose one that represents your energy
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        `;
        
        overlay.component.setContent(colorHTML);
    }
    
    /**
     * Generate philosophical insight using existing Claude API logic
     * @private
     */
    async _generatePhilosophicalInsight() {
        if (this.isRateLimited) {
            this._showRateLimitedMessage();
            return;
        }
        
        // Show loading interface
        this._showLoadingInterface();
        
        // Use existing Claude API service and logic from original PhilosophyFlowScene
        // REMOVED: this.errorBoundary.wrapAsync - using direct implementation instead
        let quoteResult;
        try {
            // Build prompt using session data
            const prompt = this._buildPhilosophicalPrompt();
            
            // Use existing Claude service
            quoteResult = await this.claudeService.generatePhilosophicalQuote({
                feeling: this.sessionData.selectedEmotion,
                mood: this.sessionData.selectedColor?.mood || 'neutral',
                color: this.sessionData.selectedColor?.hex || '#FFFFFF',
                sleep: this.sessionData.selectedSleep || 'normal'
            });
        } catch (error) {
            console.error('Error generating philosophical quote:', error);
            quoteResult = null;
        }
        
        if (quoteResult) {
            this.sessionData.quoteData = quoteResult;
            this._showPhilosophicalInsight(quoteResult);
            await this._saveQuoteTimestamp(); // Save to Firebase like original
        } else {
            this._showGenerationError();
        }
    }
    
    /**
     * Build philosophical prompt from session data
     * @returns {string} Prompt for Claude API
     * @private
     */
    _buildPhilosophicalPrompt() {
        const { selectedCategory, selectedEmotion, selectedColor } = this.sessionData;
        
        return `
            Generate a philosophical insight for an executive who is:
            - Emotional category: ${selectedCategory}
            - Specific emotion: ${selectedEmotion}
            - Energy color: ${selectedColor.name} (${selectedColor.mood})
            
            Provide a meaningful quote with author and brief reasoning suitable for C-suite professionals.
        `;
    }
    
    /**
     * Show loading interface
     * @private
     */
    _showLoadingInterface() {
        const overlay = this.componentManager.get(`${this.scene.key}_mobileOverlay`);
        if (!overlay) return;
        
        const loadingHTML = `
            <div class="game-content" style="padding-top: 120px; text-align: center;">
                <h2 class="screen-title">Generating Your Insight</h2>
                <p class="screen-subtitle">Creating a personalized philosophical reflection...</p>
                
                <div style="margin: 60px 0;">
                    <div style="font-size: 48px; animation: spin 2s linear infinite;">🧠</div>
                    <div style="margin-top: 20px; color: var(--text-muted);">
                        Analyzing your emotional state and preferences...
                    </div>
                </div>
            </div>
            
            <style>
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
            </style>
        `;
        
        overlay.component.setContent(loadingHTML);
    }
    
    /**
     * Show philosophical insight
     * @param {Object} quoteData - Generated quote data
     * @private
     */
    _showPhilosophicalInsight(quoteData) {
        const overlay = this.componentManager.get(`${this.scene.key}_mobileOverlay`);
        if (!overlay) return;
        
        const insightHTML = `
            <div class="game-content" style="padding: var(--space-2xl); padding-top: 120px;">
                <h2 class="screen-title">Your Philosophical Insight</h2>
                <p class="screen-subtitle">Based on your emotional intelligence assessment</p>

                <div class="quote-card" style="
                    background: linear-gradient(135deg, var(--card-primary), rgba(0, 212, 255, 0.05));
                    backdrop-filter: blur(20px);
                    border: 1px solid rgba(255, 255, 255, 0.15);
                    border-radius: var(--radius-lg);
                    padding: var(--space-2xl);
                    margin-bottom: var(--space-2xl);
                    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
                ">
                    <div class="quote-text" style="
                        font-size: 20px;
                        line-height: 1.6;
                        font-weight: 500;
                        margin-bottom: var(--space-xl);
                        color: var(--text-primary);
                    ">
                        "${quoteData.quote}"
                    </div>
                    <div class="quote-author" style="
                        font-size: 16px;
                        font-weight: 600;
                        color: var(--primary-solid);
                        margin-bottom: var(--space-xs);
                    ">
                        ${quoteData.author}
                    </div>
                    <div class="quote-context" style="
                        font-size: 14px;
                        color: var(--text-muted);
                    ">
                        ${quoteData.context || ''}
                    </div>
                </div>

                <button class="btn-primary" data-action="continue" style="
                    width: 100%;
                    padding: var(--space-lg);
                    background: var(--primary-gradient);
                    color: white;
                    border: none;
                    border-radius: var(--radius-md);
                    font-size: 16px;
                    font-weight: 600;
                    cursor: pointer;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                ">
                    Continue to Next Round
                </button>
            </div>
        `;
        
        overlay.component.setContent(insightHTML);
        
        // Save quote timestamp for rate limiting
        this._saveQuoteTimestamp();
    }
    
    /**
     * Show rate limited message
     * @private
     */
    _showRateLimitedMessage() {
        const overlay = this.componentManager.get(`${this.scene.key}_mobileOverlay`);
        if (!overlay) return;
        
        const rateLimitHTML = `
            <div class="game-content" style="padding: var(--space-2xl); padding-top: 120px; text-align: center;">
                <h2 class="screen-title">Daily Limit Reached</h2>
                <p class="screen-subtitle">You've completed your daily Philosophy Flow session</p>
                
                <div style="margin: 40px 0;">
                    <div style="font-size: 48px; margin-bottom: 20px;">⏰</div>
                    <p style="color: var(--text-muted);">
                        Come back tomorrow for your next philosophical insight.
                    </p>
                </div>
                
                <button class="btn-primary" data-action="back-dashboard">
                    Return to Dashboard
                </button>
            </div>
        `;
        
        overlay.component.setContent(rateLimitHTML);
    }
    
    /**
     * Show generation error
     * @private
     */
    _showGenerationError() {
        const overlay = this.componentManager.get(`${this.scene.key}_mobileOverlay`);
        if (!overlay) return;
        
        const errorHTML = `
            <div class="game-content" style="padding: var(--space-2xl); padding-top: 120px; text-align: center;">
                <h2 class="screen-title">Unable to Generate Insight</h2>
                <p class="screen-subtitle">We're experiencing technical difficulties</p>
                
                <div style="margin: 40px 0;">
                    <div style="font-size: 48px; margin-bottom: 20px;">⚠️</div>
                    <p style="color: var(--text-muted);">
                        Please try again later or contact support if the issue persists.
                    </p>
                </div>
                
                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: var(--space-md);">
                    <button class="btn-secondary" data-action="retry">Retry</button>
                    <button class="btn-primary" data-action="back-dashboard">Dashboard</button>
                </div>
            </div>
        `;
        
        overlay.component.setContent(errorHTML);
    }
    
    /**
     * Save quote timestamp for rate limiting
     * @private
     */
    async _saveQuoteTimestamp() {
        const user = auth.currentUser;
        if (!user) return;
        
        try {
            await db.collection("userProfiles").doc(user.uid).update({
                lastPhilosophyQuoteTimestamp: firebase.firestore.FieldValue.serverTimestamp()
            });
            
            console.log('✅ Quote timestamp saved');
        } catch (error) {
            console.warn('Failed to save quote timestamp:', error);
        }
    }
    
    /**
     * Show category screen (for mobile navigation)
     * @private
     */
    _showCategoryScreen() {
        const overlay = this.componentManager.get(`${this.scene.key}_mobileOverlay`);
        if (overlay) {
            overlay.component._showCategoryScreen();
        }
    }
    
    /**
     * Return to authenticated menu
     * @private
     */
    _returnToAuthenticatedMenu() {
        // Cleanup components
        this.componentManager.cleanupScene(this.scene.key);
        
        // Navigate back to appropriate scene based on device
        const targetScene = this.mobileDetection.isMobile() ? 
            'EnterpriseAuthenticatedMenuScene' : 'AuthenticatedMenuSceneDOM';
        
        this.scene.start(targetScene);
    }
    
    /**
     * Create fallback interface for errors
     * @private
     */
    _createFallbackInterface() {
        const fallbackHTML = `
            <div style="
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background: var(--background-primary);
                display: flex;
                align-items: center;
                justify-content: center;
                flex-direction: column;
                color: white;
                font-family: 'Segoe UI', sans-serif;
                z-index: 1000;
            ">
                <div style="text-align: center; max-width: 400px; padding: 40px;">
                    <h1 style="margin-bottom: 20px; color: #00d4ff;">Philosophy Flow</h1>
                    <p style="margin-bottom: 30px; opacity: 0.8;">
                        Unable to load the mobile interface. Please try refreshing or use desktop mode.
                    </p>
                    <button onclick="window.location.reload()" style="
                        background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%);
                        color: white;
                        border: none;
                        padding: 12px 24px;
                        border-radius: 8px;
                        cursor: pointer;
                        font-size: 16px;
                    ">
                        Refresh Page
                    </button>
                </div>
            </div>
        `;
        
        const fallbackElement = document.createElement('div');
        fallbackElement.innerHTML = fallbackHTML;
        document.body.appendChild(fallbackElement);
    }
    
    /**
     * Generate session ID
     * @returns {string} Unique session ID
     * @private
     */
    _generateSessionId() {
        return `pf_${Date.now()}_${Math.random().toString(36).substring(7)}`;
    }
    
    /**
     * Handle initialization failure
     * @private
     */
    _handleInitializationFailure() {
        console.error('EnterprisePhilosophyFlowScene: Initialization failed, creating fallback');
        this._createFallbackInterface();
    }
    
    /**
     * Shutdown with comprehensive cleanup
     */
    shutdown() {
        console.log("EnterprisePhilosophyFlowScene: Shutting down");
        
        try {
                // Cleanup mobile strategy
                if (this.mobileStrategy) {
                    this.mobileStrategy.cleanup();
                    this.mobileStrategy = null;
                }
                
                // Cleanup all scene components
                this.componentManager.cleanupScene(this.scene.key);
                
                // Unsubscribe from events
                this.eventSubscriptions.forEach(unsubscribe => {
                    if (typeof unsubscribe === 'function') {
                        unsubscribe();
                    }
                });
                this.eventSubscriptions.length = 0;
                
                // Clear dependencies
                this.dependencies.clear();
                
                console.log("EnterprisePhilosophyFlowScene: Shutdown complete");
        } catch (error) {
            console.warn('Error during Philosophy Flow shutdown:', error);
        }
    }
    
    /**
     * Destroy with enterprise cleanup
     */
    destroy() {
        console.log("EnterprisePhilosophyFlowScene: Destroying");
        
        try {
                // Perform shutdown cleanup first
                this.shutdown();
                
                // Final cleanup
                this.isInitialized = false;
                this.sessionData = null;
                
                // Call parent destroy
                super.destroy();
                
                console.log("EnterprisePhilosophyFlowScene: Destroy complete");
        } catch (error) {
            console.warn('Error during Philosophy Flow destruction:', error);
        }
    }
    
    /**
     * Get scene metrics for monitoring
     * @returns {Object} Scene metrics
     */
    getMetrics() {
        return {
            sceneKey: this.scene.key,
            sessionId: this.sessionData?.sessionId,
            currentStep: this.currentStep,
            isInitialized: this.isInitialized,
            isMobile: this.mobileDetection.isMobile(),
            hasStrategy: !!this.mobileStrategy,
            componentCount: this.componentManager.getComponentsForScene(this.scene.key).length,
            dependencyCount: this.dependencies.size,
            subscriptionCount: this.eventSubscriptions.length,
            isRateLimited: this.isRateLimited,
            isAdmin: this.isAdmin
        };
    }
}
